import React, { createContext, useContext, useState, useEffect } from 'react';
import api, { getToken, setToken, removeToken, setStoredUser, removeStoredUser } from '../lib/api';
import { useTheme } from './ThemeContext';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const { initThemeFromUser } = useTheme();

  useEffect(() => {
    const token = getToken();
    if (!token) {
      setLoading(false);
      return;
    }

    api.get('/auth/me')
      .then((res) => {
        const validUser = res.data;
        setStoredUser(validUser);
        setUser(validUser);
        try { initThemeFromUser(validUser.theme); } catch {}
      })
      .catch(() => {
        removeToken();
        removeStoredUser();
        setUser(null);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [initThemeFromUser]);

  const login = async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    const { token, user: userData } = response.data;
    setToken(token);
    setStoredUser(userData);
    setUser(userData);
    try { initThemeFromUser(userData.theme); } catch {}
    return userData;
  };

  const logout = () => {
    removeToken();
    removeStoredUser();
    setUser(null);
  };

  const value = {
    user,
    login,
    logout,
    loading,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
